import { forwardRef, useId, useRef, useState } from "react";
import { cn } from "../utils/cn";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "size"> {
  /** Static label displayed above the field */
  label?: string;
  /** Supplemental text shown below the field */
  helperText?: string;
  /** Validation error — replaces helperText and turns the border red */
  error?: string;
  /** Enables character counter; turns red when within 20 of the limit */
  maxLength?: number;
  /** Grows with content up to 400 px, then scrolls */
  autoResize?: boolean;
  /** Fixed number of visible rows (ignored when autoResize is true) */
  rows?: number;
  /** Controls font size and padding */
  size?: "sm" | "md" | "lg";
}

// ── Size tokens ───────────────────────────────────────────────────────────────

const SIZE = {
  sm: {
    textarea: "text-sm",
    padding:  "py-2 px-3",
  },
  md: {
    textarea: "text-base",
    padding:  "py-2.5 px-3",
  },
  lg: {
    textarea: "text-lg",
    padding:  "py-3 px-4",
  },
} as const;

// ── Component ─────────────────────────────────────────────────────────────────

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      helperText,
      error,
      maxLength,
      autoResize = false,
      rows = 3,
      size = "md",
      className,
      disabled,
      id: idProp,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      ...rest
    },
    forwardedRef,
  ) => {
    const generatedId = useId();
    const id = idProp ?? generatedId;

    const internalRef = useRef<HTMLTextAreaElement>(null);

    const [isFocused, setIsFocused] = useState(false);
    const [charCount, setCharCount] = useState(
      () => String(defaultValue ?? "").length,
    );

    const isControlled = value !== undefined;
    const hasError = !!error;
    const s = SIZE[size];

    // Character count for maxLength
    const currentCharCount = isControlled ? String(value).length : charCount;
    const nearLimit = maxLength !== undefined && currentCharCount >= maxLength - 20;
    const atLimit   = maxLength !== undefined && currentCharCount >= maxLength;

    // ── Border / ring classes ─────────────────────────────────────────────────

    const wrapperBorder = hasError
      ? "border-red-400 dark:border-red-500"
      : isFocused
        ? "border-brand-purple dark:border-violet-500"
        : "border-border-default dark:border-border-strong";

    const wrapperRing = isFocused
      ? hasError
        ? "ring-2 ring-red-400/20 dark:ring-red-400/30"
        : "ring-2 ring-brand-purple/20 dark:ring-brand-purple/30"
      : "";

    // ── Resize helper ─────────────────────────────────────────────────────────

    function syncHeight(el: HTMLTextAreaElement) {
      if (!autoResize) return;
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 400)}px`;
    }

    // ── Event handlers ────────────────────────────────────────────────────────

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (!isControlled) setCharCount(e.target.value.length);
      syncHeight(e.target);
      onChange?.(e);
    };

    // Set up forwarded + internal ref
    const setRef = (el: HTMLTextAreaElement | null) => {
      (internalRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
      if (typeof forwardedRef === "function") {
        forwardedRef(el);
      } else if (forwardedRef) {
        (forwardedRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
      }
      if (el && autoResize) syncHeight(el);
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
      <div className={cn("flex flex-col gap-1", className)}>
        {/* Label — outside the box, same pattern as Select */}
        {label && (
          <label
            htmlFor={id}
            className="text-xs font-medium text-content-secondary"
          >
            {label}
          </label>
        )}

        {/* Wrapper — border + ring + bg */}
        <div
          className={cn(
            "relative w-full rounded-md border transition-all duration-150 overflow-hidden",
            "bg-surface dark:bg-surface-raised",
            wrapperBorder,
            wrapperRing,
            disabled && "bg-surface-sunken dark:bg-surface-sunken cursor-not-allowed opacity-60",
          )}
        >
          {/* Textarea */}
          <textarea
            ref={setRef}
            id={id}
            disabled={disabled}
            value={value}
            defaultValue={defaultValue}
            rows={autoResize ? undefined : rows}
            maxLength={maxLength}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            aria-invalid={hasError}
            aria-describedby={
              error ? `${id}-error` : helperText ? `${id}-helper` : undefined
            }
            style={autoResize ? { overflow: "hidden", resize: "none" } : undefined}
            className={cn(
              "w-full bg-transparent outline-none resize-y",
              "text-content-primary placeholder:text-content-tertiary",
              "disabled:cursor-not-allowed",
              s.textarea,
              s.padding,
              // Extra bottom padding when character counter is shown
              maxLength ? "pb-6" : "",
            )}
            {...rest}
          />

          {/* Character counter — positioned inside the wrapper bottom-right */}
          {maxLength !== undefined && (
            <span
              className={cn(
                "absolute bottom-1.5 right-2.5 text-2xs select-none pointer-events-none",
                atLimit
                  ? "text-red-500 dark:text-red-400 font-medium"
                  : nearLimit
                    ? "text-brand-orange dark:text-orange-400"
                    : "text-content-tertiary",
              )}
              aria-live="polite"
              aria-label={`${currentCharCount} of ${maxLength} characters used`}
            >
              {currentCharCount}/{maxLength}
            </span>
          )}
        </div>

        {/* Error / helper text */}
        {(error || helperText) && (
          <p
            id={error ? `${id}-error` : `${id}-helper`}
            role={error ? "alert" : undefined}
            className={cn(
              "text-xs leading-snug animate-slide-down",
              error ? "text-red-600 dark:text-red-400" : "text-content-secondary",
            )}
          >
            {error ?? helperText}
          </p>
        )}
      </div>
    );
  },
);

Textarea.displayName = "Textarea";
